/**
 * @module support/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any */ // @TODO fix me
/* tslint:disable:max-file-line-count */
// We are not able to get the exact type of variable so need to discuss with concerned developer
// Core Libraries
import { EventEmitter, Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  BehaviorSubject,
  of as observableOf,
  zip as observableZip,
  throwError as observableThrowError,
} from 'rxjs';

// Services
import { LoggerService } from '@core/logger/services/logger.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { Wove } from 'aspect.js/';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
// Constants
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import {
  USER_MODULE_COMPONENTS,
  PAYMENT_CONSTANTS,
  HEADER_CONSTANTS,
  USER_SERVICE_CONSTANTS,
  UMTS_CONSTANTS,
  DATA_ONLY_PLANS,
} from '@support/shared/constants/user.constants';

// Interface
import {
  API331Params,
  Role,
} from '@experience/repdash/interfaces/API-331-get-role-permissions.interface';
import { APIError } from '@support/api/common/error.interface';
import { TopMenuUserData } from '@experience/repdash/interfaces/top-menu-user-data.interface';
import { UserData } from '@support/shared/interfaces/user-data.interface';
import { CustomerInfo } from '@support/api/proxies/responses/api-448.interface';
import { AccountInfo } from '@support/api/proxies/responses/api-451.interface';
import { LinesInfo } from '@experience/repdash/interfaces/API-231-get-lines.interface';
import { Line as Details } from '@experience/landing/common/components/account-landing/interfaces/account-lines-device-details.interface';
import {
  AccountDetails,
  CustomerDetailsResponse,
  LineDetails,
} from '@support/api/proxies/responses/api-2007.interface';
import { CustomerDetailsRequest } from '@support/api/proxies/requests/api-2007.interface';
import { BodyParams } from '@support/api/proxies/requests/api-2006.interface';
import {
  BillingAddress,
  CustomerAccountBillingDetails,
  LineSummary,
} from '@support/api/proxies/responses/api-2006.interface';
import { Api2008Request } from '@support/api/proxies/requests/api-2008.interface';
import { Api2009Request } from '@support/api/proxies/requests/api-2009.interface';
import { CustomerLineLevelSessionPassDetailsRequest } from '@support/api/proxies/requests/api-2010.interface';
import { CustomerLineBillingDetails } from '@support/api/proxies/responses/api-2008.interface';
import { CustomerSessionPassDetails } from '@support/api/proxies/responses/api-2010.interface';
import { CustomerUsageSummaryResults } from '@support/api/proxies/responses/api-2009.interface';
import { CustomerRecentCharges } from '@support/api/proxies/responses/api-2018.interface';
import {
  CustomerAccountLineDeviceDetails,
  Line,
} from '@support/api/proxies/responses/api-2022.interface';
import { CustomerNotifications } from '@support/api/proxies/responses/api-2025.interface';
import { AccountLineSeviceEIPdetails } from '@support/api/proxies/responses/api-2026.interface';
import { CustomerAccountLineServiceDeposit } from '@support/api/proxies/responses/api-2023.interface';
import { Api2022Request } from '@support/api/proxies/requests/api-2022.interface';
import { Api2025Request } from '@support/api/proxies/requests/api-2025.interface';
import { Api2817Request } from '@support/api/proxies/requests/api-2817.interface';
import { Api2026Request } from '@support/api/proxies/requests/api-2026.interface';
import { Api2023Request } from '@support/api/proxies/requests/api-2023.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { AutoPayDetails } from '@support/api/proxies/responses/api-216.interface';
import { StoredPayments } from '@support/api/proxies/responses/api-144.interface';
import { Api2032Request } from '@support/api/proxies/requests/api_2032.interface';
import { SpecialInstructions } from '@support/api/proxies/responses/api-2032.interface';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import moment from 'moment';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { CustomerAccountLineBillingDetails } from '@support/api/proxies/responses/api-2040.interface';
import { Api2040Request } from '@support/api/proxies/requests/api-2040.interface';
import { OrderSearchResponse } from '@support/api/proxies/responses/api-2813.interface';
import { SpecialInstructionsType } from '@support/api/proxies/responses/api-2047.interface';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import { ChangeLineCartRequest } from '@support/api/proxies/requests/api-2033-cart.interface';
import { ChangeLineCartResponse } from '@support/api/proxies/responses/api-2033-cart.interface';
import { MODIFY_SELECT_LINES_CONSTANTS } from '@experience/modify-cancellation/constants/modify-select-lines.constants';
import { Api2018Request } from '@support/api/proxies/requests/api-2018.interface';
import { TotalPendingOrdersResponse } from '@support/api/proxies/responses/api-2817.interface';
import { BillingTypeEnum } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { CustomerSearchRequest } from '@support/api/proxies/requests/api-2005.interface';
import { map, catchError } from 'rxjs/operators';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { SAVED_PAYMENT_CARD_CONSTANTS } from '@support/payment-cards/constants/payment-cards.constants';
import { AppConfig } from '@root/app.config';

/**
 * User service. function related to User of customer and account
 */

@Wove({ moduleName: USER_MODULE_COMPONENTS.USER_MODULE_NAME })
@Injectable(
  { providedIn: 'root' }
)
export class UserService {
  /**
   * Event Emitter to trigger events on info changes
   */
  public infoChangeEmitter: EventEmitter<Object>;
  public infoChangeSubject: Subject<Object>;
  public customerDetailsResponse: CustomerDetailsResponse;
  public context: UserData;
  public topMenuAccountDetailsUpdateSubject: Subject<TopMenuUserData>;
  public loadSpinnerSubject: BehaviorSubject<boolean>;
  public loadSpinnerSubject$: Observable<boolean>;
  public loadVerificationSpinnerSubject: BehaviorSubject<boolean>;
  public loadVerificationSpinnerSubject$: Observable<boolean>;
  public isUserLookedUp: boolean;
  public multilineMsisdnSelected: Subject<LineSummary>;
  public multilineMsisdnSelectedSubject$: Observable<LineSummary>;
  public isAllLineCancelled: boolean;
  public isBillingdetail: BehaviorSubject<boolean>;
  // public isWebChannel: boolean;
  private profileId: string;
  private lineStatus: Map<string, string> = new Map<string, string>();
  public p10CustomerLoggedIn: BehaviorSubject<boolean>;
  public p10CustomerLoggedIn$: Observable<boolean>;

  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private apiGateway: ApiGatewayService,
    private logger: LoggerService,
    private commonUtil: CommonUtilService,
    private objectService: ObjectService,
    private featureToggleService: FeatureToggleUtilService,
    private appConfig: AppConfig
  ) {
    this.infoChangeEmitter = new EventEmitter();
    this.context = {};
    this.infoChangeSubject = new Subject();
    this.loadSpinnerSubject = new BehaviorSubject(true);
    this.loadSpinnerSubject$ = this.loadSpinnerSubject.asObservable();
    this.loadVerificationSpinnerSubject = new BehaviorSubject(true);
    this.loadVerificationSpinnerSubject$ = this.loadVerificationSpinnerSubject.asObservable();
    this.multilineMsisdnSelected = new Subject();
    this.multilineMsisdnSelectedSubject$ = this.multilineMsisdnSelected.asObservable();
    this.topMenuAccountDetailsUpdateSubject = new Subject();
    this.isUserLookedUp = false;
    this.setLineSubStatus();
    this.isAllLineCancelled = false;
    this.p10CustomerLoggedIn = new BehaviorSubject(false);
    this.p10CustomerLoggedIn$ = this.p10CustomerLoggedIn.asObservable();
  }

  public getUserData(key: string): any {
    if (key) {
      return this.context[key];
    } else {
      return undefined;
    }
  }

  public getUserDataInfo(key: string): CustomerInfo {
    if (key) {
      return this.context[key];
    } else {
      return undefined;
    }
  }

  public getUserDataAccount(key: string): AccountInfo {
    if (key) {
      return this.context[key];
    } else {
      return undefined;
    }
  }

  public getUserDataLines(key: string): LinesInfo {
    if (key) {
      return this.context[key];
    } else {
      return undefined;
    }
  }

  /**
   * Method is used to delete stored cache data
   * @param {string} customerId
   */
  public removeUserData(customerId?: string): void {
    this.stateStoreUtilsService.deleteCustomerData(customerId);
  }

  /**
   * API 2008 call to get customer line BillingDetails
   * @param lineId
   * @param msisdn
   * @returns  Observable<CustomerLineBillingDetails>
   */

  /*tslint:disable cyclomatic-complexity*/
  public getCustomerLineBillingDetails(): Observable<CustomerLineBillingDetails> {
    const lineBillingDetails = this.stateStoreUtilsService.getLineBillingDetails();
    if (lineBillingDetails) {
      // Cached accountInfo from stateStoreService
      return observableOf(lineBillingDetails);
    } else {
      const billingBlockResponse = this.stateStoreUtilsService.getBillingBlockDetails();
      const msisdn: string[] = [];
      const lineId: string[] = [];
      const billingType: string = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus();
      const headers = new HttpHeaders().append(
        HEADER_CONSTANTS.BILLING_TYPE,
        billingType.toLocaleUpperCase()
      );
      const requestPayload: Api2008Request = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        msisdn: msisdn.concat(this.stateStoreUtilsService.getLineMsisdns()),
        lineID: lineId.concat(this.stateStoreUtilsService.getLineIds()),
      };
      if (billingBlockResponse) {
        requestPayload.nextBillCycleStartDate = moment(
          billingBlockResponse.nextBillCycleStartDate || Date.now(),
          'MM-DD-YYYY'
        ).format('MM-DD-YYYY');
        requestPayload.nextBillCycleEndDate = moment(
          billingBlockResponse.nextBillCycleEndDate || Date.now(),
          'MM-DD-YYYY'
        ).format('MM-DD-YYYY');
        requestPayload.billCycleStartDate = moment(
          billingBlockResponse.billCycleStartDate || Date.now(),
          'MM-DD-YYYY'
        ).format('MM-DD-YYYY');
        requestPayload.billCycleEndDate = moment(
          billingBlockResponse.billCycleEndDate || Date.now(),
          'MM-DD-YYYY'
        ).format('MM-DD-YYYY');
      }
      return this.apiGateway
        .postRequest(API_MAP.API_2008_CustomerLineBillingDetails, requestPayload, headers, null)
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.logger.debug(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              'received customer line billing details ' + USER_MODULE_COMPONENTS.USER_SERVICE,
              ' response ' + JSON.stringify(response)
            );
            this.stateStoreUtilsService.setLineBillingDetails(response.body);
            return response.body;
          }),
          catchError((error: APIError[]) => {
            this.logger.error(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              ' API error for getCustomerLineBillingDetails() in ' +
                USER_MODULE_COMPONENTS.USER_SERVICE +
                ' error: ' +
                JSON.stringify(error),
              error && error.length ? error[0].code : ''
            );
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * API 2009 call to get Line Level usage details
   * @param requestPayload
   * @returns  Observable<CustomerUsageSummaryResults>
   */
  public getUsageDetails(msisdn?: string): Observable<CustomerUsageSummaryResults> {
    let phoneNumber;
    msisdn ? (phoneNumber = msisdn) : (phoneNumber = this.stateStoreUtilsService.getMsisdn());
    const lineUsageDetails = this.stateStoreUtilsService.getUsageDetails(phoneNumber);
    if (lineUsageDetails) {
      // Cached accountInfo from stateStoreService
      return observableOf(lineUsageDetails);
    } else {
      const msisdn: string[] = [];
      const billingType: string = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus();
      const headers = new HttpHeaders().append(
        HEADER_CONSTANTS.BILLING_TYPE,
        billingType.toLocaleUpperCase()
      );
      const requestPayload: Api2009Request = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        msisdn: msisdn.concat(this.stateStoreUtilsService.getMsisdn()),
      };
      return this.apiGateway
        .postRequest(
          API_MAP.API_2009_customerAccountLineUsageDetails,
          requestPayload,
          headers,
          null,
          null
        )
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.stateStoreUtilsService.setUsageDetails(
              response.body,
              this.stateStoreUtilsService.getMsisdn()
            );
            return response.body;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * API 2010 call to get session pass details
   * @return {Observable<CustomerSessionPassDetails>}
   */
  public customerAccountLineSessionPassDetails(): Observable<CustomerSessionPassDetails> {
    const phoneNumber = this.stateStoreUtilsService.getMsisdn();
    const sessionPassDetails = this.stateStoreUtilsService.getSessionPassDetails(phoneNumber);
    const billingType: string = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus();
    const headers = new HttpHeaders().append(
      HEADER_CONSTANTS.BILLING_TYPE,
      billingType.toLocaleUpperCase()
    );
    if (sessionPassDetails) {
      // Cached accountInfo from stateStoreService
      return observableOf(sessionPassDetails);
    } else {
      const msisdn: string[] = [];
      const lineId: string[] = [];
      const requestPayload: CustomerLineLevelSessionPassDetailsRequest = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        msisdn: msisdn.concat(this.stateStoreUtilsService.getMsisdn()),
        lineOfServiceId: lineId.concat(this.stateStoreUtilsService.getLineIds()),
      };
      return this.apiGateway
        .postRequest(
          API_MAP.API_2010_customerAccountLineSessionPassDetails,
          requestPayload,
          headers,
          null
        )
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.logger.debug(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              'received customer account line session pass details ' +
                USER_MODULE_COMPONENTS.USER_SERVICE,
              ' response ' + JSON.stringify(response)
            );
            this.stateStoreUtilsService.setSessionPassDetails(
              response.body,
              this.stateStoreUtilsService.getMsisdn()
            );
            return response.body;
          }),
          catchError((error: APIError[]) => {
            this.logger.error(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              ' API error for customerAccountLineSessionPassDetails() in ' +
                USER_MODULE_COMPONENTS.USER_SERVICE +
                ' error: ' +
                JSON.stringify(error),
              error && error.length ? error[0].code : ''
            );
            return observableThrowError(error);
          })
        );
    }
  }

  public setUserData(key: string, value: any): void {
    if (key === 'searchMSISDN') {
      this.context = {};
      this.infoChangeEmitter.emit({ customerInfo: null, customerAccountInfo: null });
    }
    this.context[key] = value;
  }

  /**
   * API call for to get Customer Information
   * @param role
   */
  public getCustomerInfo(selectedRole: Role): Observable<HttpResponse<object>> {
    const headers = new HttpHeaders();
    return this.apiGateway
      .getRequest(API_MAP.API_448_getCustomerInfo, headers, null, {
        customerId: selectedRole.customerId,
      })
      .pipe(
        map((response: HttpResponse<HttpResponse<object>>) => {
          this.setUserData('info', response.body);
          this.infoChangeEmitter.emit({ customerInfo: response.body, customerAccountInfo: null });
          return response.body;
        })
      );
  }

  /**
   * API call for to get Customer Account Information
   * @param role
   * @param account Id
   */
  public getCustomerAccountInfo(
    selectedRole: Role,
    accountId: string
  ): Observable<HttpResponse<object>> {
    const headers = new HttpHeaders();
    let customerId: string;
    // TO DO : As per analysis, There would not be any scenario when selected role would be null.
    // So code needs to be revisited.
    if (!selectedRole) {
      customerId = sessionStorage.getItem('customerId');
      if (!customerId && this.getUserData('selectedRole')) {
        customerId = this.getUserData('selectedRole').customerId;
      }
    } else {
      customerId = selectedRole.customerId;
    }
    if (!accountId) {
      accountId = sessionStorage.getItem('accountId');
    }
    return this.apiGateway
      .getRequest(API_MAP.API_451_getCustomerAccount, headers, null, {
        customerId: selectedRole.customerId,
        accountId: accountId,
      })
      .pipe(
        map((response: HttpResponse<HttpResponse<object>>) => {
          this.setUserData('account', response.body);
          return response.body;
        })
      );
  }

  /**
   * API call for to get Customer Account Lines Information
   * @param params request params for getCustomerAccountLines API call
   */
  public getLinesData(params: API331Params): Observable<LinesInfo> {
    const linesInfo = this.stateStoreUtilsService.getLinesInfo();
    if (linesInfo) {
      return observableOf(linesInfo);
    } else {
      return this.apiGateway
        .getRequest(API_MAP.API_231_getCustomerAccountLines, null, null, params)
        .pipe(
          map((response: HttpResponse<LinesInfo>) => {
            this.stateStoreUtilsService.setLinesInfo(response.body);
            return response.body;
          })
        );
    }
  }

  /**
   * Called to bypass the verification process
   * @param selectedRole
   * @param accountId
   */
  public bypassVerification(
    selectedRole: Role,
    accountId: string
  ): Observable<HttpResponse<object>[]> {
    return observableZip(
      this.getCustomerInfo(selectedRole),
      this.getCustomerAccountInfo(selectedRole, accountId)
    );
  }

  /**
   * Sets the profile id of the user
   * @param value : profile Id
   */
  public setProfileId(value: string): void {
    this.stateStoreUtilsService.setProfileId(value);
    this.profileId = value;
  }

  /**
   * gets the profile Id of the user
   */
  public getProfileId(): string {
    return this.profileId;
  }

  /**
   * Functions retrieves customerInfo from API 448
   * - Retrieves customerID of current customer in context
   * - If customerID is valid (not anonymous), then checks StateStoreService to get customerInfo
   * - If stateStore has cached customerInfo, returns the cachedInformation
   * - Otherwise makes API request to API 448. Stores in StateStoreService and returns information
   */
  public getCustomerInfoObservable(): Observable<CustomerInfo> {
    const customerId = this.stateStoreUtilsService.getCustomerId();
    if (customerId) {
      let customerInfo = this.stateStoreUtilsService.getCustomerInfo(customerId);
      if (customerInfo) {
        // Cached customerInfo from stateStoreService
        return observableOf(customerInfo);
      } else {
        // Make API Request and store customerInfo in StateStoreService
        return this.apiGateway
          .getRequest(API_MAP.API_448_getCustomerInfo, null, null, { customerId: customerId })
          .pipe(
            map((response: HttpResponse<Object>) => {
              // TODO: Remove below line for this.setUserData as it is for legacy support
              customerInfo = response.body;
              this.setUserData('info', customerInfo);
              this.stateStoreUtilsService.setCustomerInfo(customerId, customerInfo);
              return customerInfo;
            })
          );
      }
    } else {
      // Customer is anonymous. No current customer in context
      return observableOf(null);
    }
  }

  /**
   * Functions retrieves AccountInfo from API 451
   * - Retrieves customerID and accoundID of current customer in context
   * - If customerID and/or accountID is valid (not anonymous), then checks StateStoreService to get AccountInfo
   * - If stateStore has cached AccountInfo, returns the cachedInformation
   * - Otherwise makes API request to API 451. Stores in StateStoreService and returns information
   */
  public getAccountInfoObservable(): Observable<AccountInfo> {
    let accountInfo = this.stateStoreUtilsService.getAccountInfo();
    if (accountInfo) {
      // Cached accountInfo from stateStoreService
      return observableOf(accountInfo);
    } else {
      const pathParams = {
        customerId: this.stateStoreUtilsService.getCustomerId(),
        accountId: this.stateStoreUtilsService.getAccountId(),
      };
      return this.apiGateway
        .getRequest(API_MAP.API_451_getCustomerAccount, null, null, pathParams)
        .pipe(
          map((response: HttpResponse<AccountInfo>) => {
            accountInfo = response.body;
            this.stateStoreUtilsService.setAccountInfo(accountInfo);
            return accountInfo;
          })
        );
    }
  }

  /**
   * Functions retrieves Lines Info from API 231
   * - Retrieves customerID and accoundID of current customer in context
   * - If customerID and/or accountID is valid (not anonymous), then checks StateStoreService to get Lines
   * - If stateStore has cached Lines, returns the cachedInformation
   * - Otherwise makes API request to API 231. Stores in StateStoreService and returns information
   */
  public getLinesInfoObservable(): Observable<LinesInfo> {
    let lines = this.stateStoreUtilsService.getLinesInfo();
    if (lines) {
      // Cached accountInfo from stateStoreService
      return observableOf(lines);
    } else {
      const customerId = this.stateStoreUtilsService.getCustomerId();
      const accountId = this.stateStoreUtilsService.getAccountId();
      return this.apiGateway
        .getRequest(API_MAP.API_231_getCustomerAccountLines, null, null, {
          customerId: customerId,
          accountId: accountId,
        })
        .pipe(
          map((response: HttpResponse<LinesInfo>) => {
            // TODO: Remove below line for this.setUserData as it is for legacy support
            lines = response.body;
            this.stateStoreUtilsService.setLinesInfo(lines);
            return lines;
          })
        );
    }
  }

  /**
   * Functions retrieves customer notifications from API 342
   * - No caching enabled for this API call as data can consistently change
   */
  // This function is not in use.
  // public getNotificationsObservable(): Observable<Account> {
  //   const customerId = this.stateStoreUtilsService.getCustomerId();
  //   if (customerId) {
  //     return this.apiGateway.getRequest(
  //       API_MAP.API_342_getNotifications,
  //       null,
  //       null,
  //       { customerId: customerId }).pipe(
  //         map((response: HttpResponse<Object>) => {
  //           const data = response.body;
  //           // TODO: Vefify why only first account
  //           return (data && data.accounts && (data.accounts.length > 0)) ? data.accounts[0] : [];
  //         }), catchError((error: APIError[]) => {
  //           return observableThrowError(error);
  //         })
  //       );
  //   } else {
  //     // Customer is anonymous. No current customer in context
  //     return observableOf(null);
  //   }
  // }

  /**
   * API Call to get Customer Account Billing Details
   * @returns Observable<CustomerAccountBillingDetails>
   */
  public getCustomerAccountBillingSummary(): Observable<CustomerAccountBillingDetails> {
    const billingBlockData = this.stateStoreUtilsService.getBillingBlockDetails();
    const programIndicator: string = this.objectService.get(
      billingBlockData,
      'accountSegmentation.accountLifeLineAssistanceProgramIndicator'
    );
    const isP10MCustomer: boolean = programIndicator
      ? programIndicator.toLocaleLowerCase() === USER_SERVICE_CONSTANTS.P10M_CUSTOMER
      : false;
    if (programIndicator) {
      this.p10CustomerLoggedIn.next(isP10MCustomer);
    }
    if (billingBlockData && !isP10MCustomer) {
      return observableOf(billingBlockData);
    } else {
      const payload: BodyParams = {
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        ccid: this.stateStoreUtilsService.getCustomerId(),
        losId: this.stateStoreUtilsService.getLineofServiceID(),
      };
      const billingType: string = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus();
      const headers = new HttpHeaders().append(
        HEADER_CONSTANTS.BILLING_TYPE,
        billingType.toLocaleUpperCase()
      );
      return this.apiGateway
        .postRequest(API_MAP.API_2006_customerAccountBillingSummary, payload, headers, null, null)
        .pipe(
          map((response: HttpResponse<CustomerAccountBillingDetails>) => {
            let customerAccountBillingSummaryDetail = response.body;
            this.logger.debug(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              'received customer account billing summary ' + USER_MODULE_COMPONENTS.USER_SERVICE,
              ' response ' + JSON.stringify(response)
            );
            customerAccountBillingSummaryDetail = this.formatMsisdns(
              customerAccountBillingSummaryDetail
            );
            if (customerAccountBillingSummaryDetail.fraudLockIndicator) {
              this.stateStoreUtilsService.setAccountFraudStatus(
                customerAccountBillingSummaryDetail.accountNumber,
                customerAccountBillingSummaryDetail.fraudLockIndicator
              );
            }
            const programIndicator: string = this.objectService.get(
              customerAccountBillingSummaryDetail,
              'accountSegmentation.accountLifeLineAssistanceProgramIndicator'
            );
            if (programIndicator) {
              const isP10MCustomer: boolean =
                programIndicator.toLocaleLowerCase() === USER_SERVICE_CONSTANTS.P10M_CUSTOMER;
              this.p10CustomerLoggedIn.next(isP10MCustomer);
            }
            this.stateStoreUtilsService.setBillingBlockDetails(customerAccountBillingSummaryDetail);
            // @TODO to be checked later once functionality is finalized
            this.context.isVerified = true;
            // @TODO for DE155717 : this check to be removed when API 2007 will return expected data
            if (
              customerAccountBillingSummaryDetail.lines[0] &&
              this.objectService.get(customerAccountBillingSummaryDetail.lines[0], 'msisdn')
            ) {
              const setMsisdn =
                this.objectService.get(customerAccountBillingSummaryDetail.lines[0], 'msisdn') ||
                '';
              this.stateStoreUtilsService.setMsisdn(setMsisdn);
            }
            this.extractAndCacheBillingAddress(customerAccountBillingSummaryDetail);

            this.stateStoreUtilsService.setIsManagerOverrideAllowed(
              this.isManagerOverrideAllowed(customerAccountBillingSummaryDetail)
            );
            if (this.featureToggleService.isPhoenixByodEnabled()) {
              this.checkAndSetIfDataOnlyLine(customerAccountBillingSummaryDetail);
            }
            return response.body;
          }),
          catchError((error: APIError[]) => {
            this.logger.error(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              ' API error for getCustomerAccountBillingSummary() in ' +
                USER_MODULE_COMPONENTS.USER_SERVICE +
                ' error: ' +
                JSON.stringify(error),
              error && error.length ? error[0].code : ''
            );
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Function to check if logged in user has data only line
   * setting the flag in session storage to be retrieved by phoenix BYOD
   * @param response - CustomerAccountBillingDetails
   */
  checkAndSetIfDataOnlyLine(response: CustomerAccountBillingDetails): void {
    const isDataOnlyAccount: boolean = response.lines[0].planName === DATA_ONLY_PLANS.PLAN_NAME;
    sessionStorage.setItem('dataOnlyAccount', isDataOnlyAccount.toString());
  }

  /**
   * Method to get recent charges information
   * @return Observable<CustomerRecentCharges[]>
   */
  public getRecentBills(): Observable<CustomerRecentCharges> {
    const recentBills = this.stateStoreUtilsService.getRecentBillingDetails();
    if (recentBills) {
      // Cached accountInfo from stateStoreService
      return observableOf(recentBills);
    } else {
      const requestPayload: Api2018Request = {
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        ccid: this.stateStoreUtilsService.getCustomerId(),
      };
      const headers = new HttpHeaders();
      const pathParams = {
        accountId: this.stateStoreUtilsService.getAccountId(),
      };
      return this.apiGateway
        .postRequest(API_MAP.API_2018_AccountBillSummary, requestPayload, headers, null, pathParams)
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.logger.debug(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              'get recent bill details ' + USER_MODULE_COMPONENTS.USER_SERVICE,
              ' response ' + JSON.stringify(response)
            );
            this.stateStoreUtilsService.setRecentBillingDetails(response.body);
            return response.body;
          }),
          catchError((error: APIError[]) => {
            this.logger.error(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              ' API error for getRecentBills() in ' +
                USER_MODULE_COMPONENTS.USER_SERVICE +
                ' error: ' +
                JSON.stringify(error),
              error && error.length ? error[0].code : ''
            );
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Function to be called when guest user is brought into focus
   */
  public setGuestInContext(): void {
    this.context = {};
  }

  /**
   * Method to call API 2007 getRoles
   * @param pathParams - the parameters to get request
   */
  public getCustomerDetailSearch(): Observable<CustomerDetailsResponse> {
    // To-do
    // Needed further refactoring in future once we start receiving chub id (ccid) in order search result
    const orderId = this.stateStoreUtilsService.getCustomerOrderId();
    if (this.stateStoreUtilsService.getAccountLinesInfo()) {
      return observableOf(this.stateStoreUtilsService.getAccountLinesInfo());
    } else {
      const customerDetailsRequestPayload: CustomerDetailsRequest = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        bscsCustomerID: this.stateStoreUtilsService.getBscsCustomerID(),
      };
      return this.apiGateway
        .postRequest(API_MAP.API_2007_getCustomerDetails, customerDetailsRequestPayload)
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.logger.debug(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              'received customer search details ' + USER_MODULE_COMPONENTS.USER_SERVICE,
              ' response ' + JSON.stringify(response)
            );
            this.customerDetailsResponse = response.body;
            if (
              this.commonUtil.isNotEmpty(this.customerDetailsResponse.customer) &&
              this.commonUtil.isNotEmpty(this.customerDetailsResponse.customer.email)
            ) {
              this.stateStoreUtilsService.setCustomerEmail(
                this.customerDetailsResponse.customer.email
              );
            }
            if (
              this.objectService.get(this.customerDetailsResponse, 'customer.ccid') &&
              !this.stateStoreUtilsService.getCustomerId()
            ) {
              this.stateStoreUtilsService.setRepActiveCustomerId(
                this.customerDetailsResponse.customer.ccid
              );
            }
            if (
              this.objectService.get(this.customerDetailsResponse, 'customer.bscsCustomerID') &&
              !this.stateStoreUtilsService.getBscsCustomerID()
            ) {
              this.stateStoreUtilsService.setBscsCustomerID(
                this.customerDetailsResponse.customer.bscsCustomerID
              );
            }
            this.stateStoreUtilsService.setAccountLinesInfo(this.customerDetailsResponse);
            this.stateStoreUtilsService.setCustomerOrderId(orderId);
            return response.body;
          }),
          catchError((error: APIError[]) => {
            this.logger.error(
              USER_MODULE_COMPONENTS.USER_MODULE_NAME,
              USER_MODULE_COMPONENTS.USER_SERVICE,
              ' API error for getCustomerDetailSearch() in ' +
                USER_MODULE_COMPONENTS.USER_SERVICE +
                ' error: ' +
                JSON.stringify(error),
              error && error.length ? error[0].code : ''
            );
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Method to call API 2007 getMultpleFANS
   * @param pathParams - the parameters to get request
   */
  public getMultipleFANS(): Observable<CustomerDetailsResponse> {
    const customerDetailsRequestPayload: CustomerDetailsRequest = {
      bscsCustomerID: this.stateStoreUtilsService.getBscsCustomerID(),
      accountNumber: this.stateStoreUtilsService.getAccountId(),
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2007_getCustomerDetails, customerDetailsRequestPayload)
      .pipe(
        map((response: HttpResponse<Object>) => {
          this.logger.debug(
            USER_MODULE_COMPONENTS.USER_MODULE_NAME,
            USER_MODULE_COMPONENTS.USER_SERVICE,
            'received customer search details ' + USER_MODULE_COMPONENTS.USER_SERVICE,
            ' response ' + JSON.stringify(response)
          );
          this.customerDetailsResponse = response.body;
          return response.body;
        }),
        catchError((error: APIError[]) => {
          this.logger.error(
            USER_MODULE_COMPONENTS.USER_MODULE_NAME,
            USER_MODULE_COMPONENTS.USER_SERVICE,
            ' API error for getCustomerDetailSearch() in ' +
              USER_MODULE_COMPONENTS.USER_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error && error.length ? error[0].code : ''
          );
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method to call API 2022 to get linedevice details
   * @param {string} [msisdn]
   * @return Observable<CustomerAccountLineDeviceDetails>
   */
  public getCustomerAccountLineDeviceDetails(
    msisdn?: string
  ): Observable<CustomerAccountLineDeviceDetails> {
    if (this.stateStoreUtilsService.getCustomerAccountLineDeviceDetailsData(msisdn)) {
      return observableOf(
        this.stateStoreUtilsService.getCustomerAccountLineDeviceDetailsData(msisdn)
      );
    } else {
      const customerAccountLineDeviceDetailsPayload: Api2022Request = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        msisdn: [msisdn],
      };
      // this.isWebChannel = (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB);
      const billingType: string = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus();
      const headers = new HttpHeaders().append(
        HEADER_CONSTANTS.BILLING_TYPE,
        billingType.toLocaleUpperCase()
      );
      // if (!this.isWebChannel){
      //   const dealerCode: string = this.stateStoreUtilsService.getDealerCode()
      //     ? this.stateStoreUtilsService.getDealerCode() : null;
      //   headers = headers.append(HEADER_CONSTANTS.DEALER_CODE, dealerCode);
      // }
      return this.apiGateway
        .postRequest(
          API_MAP.API_2022_CustomerAccountLineDeviceDetails,
          customerAccountLineDeviceDetailsPayload,
          headers,
          null,
          null
        )
        .pipe(
          map((response: HttpResponse<Object>) => {
            const deviceDetails = response.body;
            this.stateStoreUtilsService.setCustomerAccountLineDeviceDetailsData(
              deviceDetails,
              msisdn
            );
            return deviceDetails;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Method to call API 2025 to get account autopay status
   * @return Observable<CustomerNotifications>
   */
  public getCustomerAccountAutopayStatus(): Observable<CustomerNotifications> {
    if (this.stateStoreUtilsService.getCustomerAccountAutopayStatusData()) {
      return observableOf(this.stateStoreUtilsService.getCustomerAccountAutopayStatusData());
    } else {
      const customerAccountAutopayStatusPayload: Api2025Request = {
        ccid: this.stateStoreUtilsService.getBscsCustomerID(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        onlyAutoPayData: false,
      };
      return this.apiGateway
        .postRequest(API_MAP.API_2025_CustomerNotifications, customerAccountAutopayStatusPayload)
        .pipe(
          map((response: HttpResponse<Object>) => {
            const autoPayData = response.body;
            this.stateStoreUtilsService.setCustomerAccountAutopayStatusData(autoPayData);
            return autoPayData;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Method to call API 2026 to get Account Line devices EIP details
   * @return Observable<AccountLineSeviceEIPdetails>
   */
  public getCustomerAccountLinedevicesEIPdetails(): Observable<AccountLineSeviceEIPdetails> {
    if (this.stateStoreUtilsService.getCustomerAccountLineDevicesEIPDetails()) {
      return observableOf(this.stateStoreUtilsService.getCustomerAccountLineDevicesEIPDetails());
    } else {
      const customerAccountLineDevicesEIPDetailsPayload: Api2026Request = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
      };
      return this.apiGateway
        .postRequest(
          API_MAP.API_2026_AccountLineDeviceEIPDetails,
          customerAccountLineDevicesEIPDetailsPayload
        )
        .pipe(
          map((response: HttpResponse<Object>) => {
            const eipDetails = response.body;
            this.stateStoreUtilsService.setCustomerAccountLineDevicesEIPDetails(eipDetails);
            return eipDetails;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Method to call API 2023 to get Account Line devices service deposits details
   * @return Observable<CustomerAccountLineServiceDeposit>
   */
  public getCustomerAccountLineServiceDepositsDetails(): Observable<CustomerAccountLineServiceDeposit> {
    if (this.stateStoreUtilsService.getCustomerAccountLineServiceDepositsDetails()) {
      return observableOf(
        this.stateStoreUtilsService.getCustomerAccountLineServiceDepositsDetails()
      );
    } else {
      const customerAccountServiceDepositPayload: Api2023Request = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
      };
      return this.apiGateway
        .postRequest(
          API_MAP.API_2023_CustomerAccountLineServiceDepositDetails,
          customerAccountServiceDepositPayload
        )
        .pipe(
          map((response: HttpResponse<Object>) => {
            const serviceDepositDetails = response.body;
            this.stateStoreUtilsService.setCustomerAccountLineServiceDepositsDetails(
              serviceDepositDetails
            );
            return serviceDepositDetails;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Function to fetch current account status
   * @returns {string}
   */
  public getAccountStatusOfCustomerInFocus(): string {
    const customerInfo: CustomerDetailsResponse = this.stateStoreUtilsService.getAccountLinesInfo();
    const selectedAccId = this.stateStoreUtilsService.getAccountId();
    const accounts = this.objectService.get(customerInfo, 'customer.accounts');
    const currentAccount = accounts.find((account: AccountDetails) => {
      return account.accountNumber === selectedAccId;
    });
    const accountStatus = this.objectService.get(currentAccount, 'accountStatus');
    return accountStatus;
  }

  /**
   * Used to get AutoPay Details
   * @returns {Observable<Object>}
   */
  public getAutoPayDetails(): Observable<AutoPayDetails> {
    return this.apiGateway.getRequest(API_MAP.API_216_getAutopay).pipe(
      map((response: HttpResponse<AutoPayDetails>) => {
        this.stateStoreUtilsService.setAutoPayDetails(response.body);
        return response.body;
      })
    );
  }

  /**
   * Function to fetch special instructions from API_2032
   * @returns Observable
   */
  public getSpecialInstructions(): Observable<SpecialInstructions> {
    const specialInstructions = this.stateStoreUtilsService.getSpecialInstructions();
    if (specialInstructions) {
      return observableOf(specialInstructions);
    } else {
      const requestPayload: Api2032Request = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        bscsCustomerId: this.stateStoreUtilsService.getBscsCustomerID(),
      };
      return this.apiGateway
        .postRequest(ASSISTED_API_MAP.API_2032_postSpecialInstructions, requestPayload)
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.stateStoreUtilsService.setSpecialInstructions(response.body);
            return response.body;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Function to fetch the stored payment options
   * @returns Observable
   */
  public getStoredPaymentMethods(): Observable<StoredPayments> {
    const storedPayment = this.stateStoreUtilsService.getStoredPaymentMethods();
    if (storedPayment) {
      return observableOf(storedPayment);
    } else {
      const payload = {
        customerId: this.setCustomerID(),
        commonCustomerId: this.getCommonCustomerId(),
        accountId: this.stateStoreUtilsService.getAccountId(),
        paymentMethods: PAYMENT_CONSTANTS.PAYMENT_METHOD_NAME,
      };
      const apiToCall = API_MAP.API_2617_getStoredPaymentMethods;
      return this.apiGateway.postRequest(apiToCall, payload, null, null, null).pipe(
        map((response: HttpResponse<StoredPayments>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
    }
  }
  /**
   * set customer ID for fetch eligible payments
   */
  private setCustomerID(): string {
    return this.stateStoreUtilsService.getCustomerId() &&
      this.stateStoreUtilsService.getCustomerId() !== SAVED_PAYMENT_CARD_CONSTANTS.ANON
      ? this.stateStoreUtilsService.getCustomerId()
      : null;
  }
  /**
   * used to get common customer id
   * @returns {string}
   */
  public getCommonCustomerId(): string {
    // if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB) {
    const ccid = this.objectService.get(
      this.stateStoreUtilsService.getAccountLinesInfo(),
      'customer.ccid'
    );
    return ccid && this.stateStoreUtilsService.getCustomerId() !== SAVED_PAYMENT_CARD_CONSTANTS.ANON
      ? ccid
      : null;
    // } else {
    //   return (this.stateStoreUtilsService.getCustomerId() === SAVED_PAYMENT_CARD_CONSTANTS.ANON
    //     ? null : this.stateStoreUtilsService.getCustomerId());
    // }
  }

  /**
   * Function to verify the security pin entered
   * @param {string} pin
   * @returns Observable<CustomerDetailsResponse>
   */
  public pinVerification(pin: string): Observable<CustomerDetailsResponse> {
    const customerDetailsRequestPayload = {
      ccid: this.stateStoreUtilsService.getCustomerId(),
      securityPIN: pin,
      accountNumber: this.stateStoreUtilsService.getAccountId(),
      bscsCustomerID: this.stateStoreUtilsService.getBscsCustomerID(),
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2007_getCustomerDetails, customerDetailsRequestPayload)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * API 2040 call to get customer line BillingDetails
   * @param lineOfServiceId string
   * @returns  Observable<CustomerAccountLineBillingDetails>
   */
  public getCustomerBillingDetailsData(
    lineOfServiceId?: string
  ): Observable<CustomerAccountLineBillingDetails> {
    const customerAccountLineBillingDetail =
      this.stateStoreUtilsService.getCustomerAccountLineBillingDetail(
        lineOfServiceId ? lineOfServiceId : this.stateStoreUtilsService.getLineId()
      );
    if (customerAccountLineBillingDetail) {
      return observableOf(customerAccountLineBillingDetail);
    } else {
      const billingBlockResponse = this.stateStoreUtilsService.getBillingBlockDetails();
      const requestPayload: Api2040Request = {
        ccid: this.stateStoreUtilsService.getCustomerId(),
        accountNumber: this.stateStoreUtilsService.getAccountId(),
        lineOfServiceID: lineOfServiceId
          ? lineOfServiceId
          : this.stateStoreUtilsService.getLineId(),
        currentBillCycleEndDate: moment(
          this.objectService.get(billingBlockResponse, 'billCycleEndDate') || Date.now()
        ).format('MM/DD/YYYY'),
      };
      return this.apiGateway
        .postRequest(API_MAP.API_2040_lineBillingDetails, requestPayload, null, null)
        .pipe(
          map((response: HttpResponse<Object>) => {
            this.stateStoreUtilsService.setCustomerAccountLineBillingDetail(
              response.body,
              lineOfServiceId ? lineOfServiceId : this.stateStoreUtilsService.getLineId()
            );
            return response.body;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Method for calling API 2813  searchOrders.
   * @param lookUpParams : parameters to be passed in the get request
   * Onject for Order Id Array Response
   * @returns {Observable<OrderSearchResponse>}
   */
  public getPendingOrderResults(lookUpParams: {
    [name: string]: string;
  }): Observable<OrderSearchResponse> {
    const headers = new HttpHeaders();
    return this.apiGateway
      .postRequest(API_MAP.API_2813_getSearchOrders, lookUpParams, headers)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Function to fetch special instructions type from API_2047
   * @returns Observable
   */
  public getSpecialInstructionsType(): Observable<SpecialInstructionsType> {
    let instructionTypeInfo = this.stateStoreUtilsService.getInstructionType();
    if (instructionTypeInfo) {
      // Cached customerInfo from stateStoreService
      return observableOf(instructionTypeInfo);
    } else {
      // Make API Request and store customerInfo in StateStoreService
      return this.apiGateway
        .getRequest(ASSISTED_API_MAP.API_2047_getSpecialInstructionsType, null, null, null)
        .pipe(
          map((response: HttpResponse<Object>) => {
            instructionTypeInfo = response.body;
            this.stateStoreUtilsService.setInstructionType(instructionTypeInfo);
            return instructionTypeInfo;
          })
        );
    }
  }
  /*
   * Function to post cart request for lost-stolen
   * @param {string} imei
   * @returns {Observable<ChangeLineCartResponse>}
   */
  public postChangeLine(request: ChangeLineCartRequest): Observable<ChangeLineCartResponse> {
    // PM-207: RebellionWeb: Unsuspended reason code fix
    const billingBlockDetails = this.stateStoreUtilsService.getBillingBlockDetails();
    if (
      billingBlockDetails &&
      billingBlockDetails.lines &&
      request.changeTypes === UMTS_CONSTANTS.RESTORE
    ) {
      billingBlockDetails.lines.forEach((line: LineSummary) => {
        if (
          line.lineStatus === UMTS_CONSTANTS.SUSPENDED &&
          line.lineOfServiceId === request.lineOfServiceId &&
          line.lineSubStatus
        ) {
          request.suspensionReasonCode = line.lineSubStatus;
        }
      });
    }

    return this.apiGateway
      .postRequest(API_MAP.API_2033_changeLine, request, null, null, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }
  /*
   * Function to give focuused line's device property
   * @param {string} property msisdn
   * @returns {Observable<string>}
   */
  public getDevicePropertyForFocussedOrSelectedLineDevice(
    property: string,
    msisdn?: string
  ): Observable<string> {
    const inputMsisdn = msisdn || this.stateStoreUtilsService.getMsisdn();
    return this.getCustomerAccountLineDeviceDetails(inputMsisdn).pipe(
      map((response: CustomerAccountLineDeviceDetails) => {
        if (
          response.devicedetails &&
          response.devicedetails.lines &&
          response.devicedetails.lines.length > 0
        ) {
          const lineSelected: Line = response.devicedetails.lines.find(
            (line: Line) => line.msisdn === inputMsisdn
          );
          if (this.commonUtil.isNotEmpty(lineSelected)) {
            return lineSelected[property];
          } else {
            return undefined;
          }
        } else {
          return undefined;
        }
      }),
      catchError((error: APIError[]) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Function to get blackout period for autopay
   * @returns Observable<boolean>
   */
  public getBlackoutStatus(): Observable<boolean> {
    return this.getAutoPayDetails().pipe(
      map((data: AutoPayDetails) => {
        const isBlackout: boolean = data.blackoutPeriod === 'false' ? false : true;
        return isBlackout;
      })
    );
  }
  /**
   * function to set MAP
   * @memberof UserService
   */
  public setLineSubStatus(): void {
    this.lineStatus.set(
      MODIFY_SELECT_LINES_CONSTANTS.ELSTS,
      MODIFY_SELECT_LINES_CONSTANTS.LOST_STOLEN
    );
    this.lineStatus.set(MODIFY_SELECT_LINES_CONSTANTS.UPSUS, MODIFY_SELECT_LINES_CONSTANTS.UNPAID);
  }
  /**
   * function to get lineSubStatus
   * @param {Details} lineDetails
   * @returns {string}
   * @memberof UserService
   */
  public getLineStatus(lineDetails: Details): string {
    const lineSubStatus = lineDetails.lineSubStatus;
    return this.lineStatus.get(lineSubStatus);
  }
  /**
   * function to get selected msisdn.
   * @param {CustomerDetailsResponse} customerDetailsResponse
   * @param {string} msisdn
   * @returns {LineDetails[]}
   * @memberof UserService
   */
  public getRoleForMsisdnInFocus(
    customerDetailsResponse: CustomerDetailsResponse,
    msisdn: string
  ): LineDetails[] {
    let lineDetails: LineDetails[];
    if (this.objectService.get(customerDetailsResponse.customer, 'lines')) {
      lineDetails = customerDetailsResponse.customer.lines.filter((line: LineDetails) => {
        return line.msisdn === msisdn;
      });
    }
    return lineDetails;
  }

  /**
   * Function to extract and cache billing address from 2006 response
   */
  private extractAndCacheBillingAddress(
    customerAccountBillingDetails: CustomerAccountBillingDetails
  ): void {
    const zipLength = 5;
    const billingAddress: BillingAddress = this.objectService.get(
      customerAccountBillingDetails,
      'billingAddress'
    ) || { zip: '' };
    const customerConfigData: BrowseEntry =
      this.stateStoreUtilsService.getCustomerConfigData() || {};
    const address: Address = customerConfigData.addresses || {};
    address.addressLine1 = billingAddress.addressLine1;
    address.addressLine2 = billingAddress.addressLine2;
    address.cityName = billingAddress.cityName;
    address.postalCode =
      billingAddress.zip.length > zipLength
        ? billingAddress.zip.slice(0, zipLength)
        : billingAddress.zip;
    address.stateCode = billingAddress.stateCode;
    customerConfigData.zip = address.postalCode;
    customerConfigData.addresses = address;
    customerConfigData.billingType = this.objectService.get(
      customerAccountBillingDetails,
      'billingType'
    )
      ? customerAccountBillingDetails.billingType.toLowerCase() ===
        MODIFY_SELECT_LINES_CONSTANTS.PREPAID
        ? BillingTypeEnum.PREPAID
        : BillingTypeEnum.POSTPAID
      : BillingTypeEnum.PREPAID;
    this.stateStoreUtilsService.setCustomerConfigData(customerConfigData);
    this.stateStoreUtilsService.setBillingAddress(customerConfigData.addresses);
    this.stateStoreUtilsService.setAuthBillingAddress(customerConfigData.addresses);
  }
  /**
   * Function to format msisdns from 2006 response
   */
  private formatMsisdns(
    customerAccountBillingDetails: CustomerAccountBillingDetails
  ): CustomerAccountBillingDetails {
    customerAccountBillingDetails.lines.forEach((line: LineSummary) => {
      line.msisdn = this.commonUtil.formatMsisdn10Digits(line.msisdn);
    });
    return customerAccountBillingDetails;
  }
  /**
   * Method to get Total Pending Order Count for MSISDN lines
   */
  public getTotalPendingOrderCount(): Observable<TotalPendingOrdersResponse> {
    const requestPayload: Api2817Request = {
      ccid: this.stateStoreUtilsService.getCustomerId(),
      accountNumber: this.stateStoreUtilsService.getAccountId(),
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2817_getTotalPending, requestPayload, null, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Method to create customer search request
   * @param lookUpParams : parameters to be passed in the post request
   * @returns CustomerSearchRequest
   */
  public createCustomerSearchRequest(lookUpParams: {
    [name: string]: string;
  }): CustomerSearchRequest {
    this.createPhoneFormat(lookUpParams);
    const reqObj: CustomerSearchRequest = {
      email: this.objectService.get(lookUpParams, 'email'),
      accountNumber: this.objectService.get(lookUpParams, 'accntNo'),
      ssn: this.objectService.get(lookUpParams, 'ssn'),
      lastFourSSN: this.objectService.get(lookUpParams, 'ssnLast4'),
      firstName: this.objectService.get(lookUpParams, 'firstName'),
      lastName: this.objectService.get(lookUpParams, 'lastName'),
      msisdn: this.objectService.get(lookUpParams, 'msisdn'),
      imei: this.objectService.get(lookUpParams, 'imei'),
      sim: this.objectService.get(lookUpParams, 'sim'),
      addressLine1: this.objectService.get(lookUpParams, 'street'),
      city: this.objectService.get(lookUpParams, 'city'),
      state: this.objectService.get(lookUpParams, 'state'),
      zipcode: this.objectService.get(lookUpParams, 'zip'),
      authorizedBusinessFlag: '',
      startRowNumber: '',
      sortBy: '',
    };
    return reqObj;
  }
  /**
   * Method to create required phone format
   * @param lookUpParams : parameters to be passed in the post request
   * @returns void
   */
  private createPhoneFormat(lookUpParams?: { [name: string]: string }): void {
    if (lookUpParams.msisdn) {
      lookUpParams.msisdn = lookUpParams.msisdn.replace(/\D+/g, '');
    }
  }

  /**
   * Method for calling API 2005 searchCustomers
   * @param lookUpParams : parameters to be passed in the get request
   */
  public getCustomers(lookUpParams: { [name: string]: string }): Observable<Object> {
    const searchRequest: CustomerSearchRequest = this.createCustomerSearchRequest(lookUpParams);
    return this.apiGateway
      .postRequest(ASSISTED_API_MAP.API_2005_postSearchCustomers, searchRequest)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method for calling API 2827 searchCustomers
   * @param msisdn : parameters to be passed in the request payload
   */
  public getMsisdnChangeElgilibilityStatus(msisdn: string): Observable<Object> {
    const payload = {
      billingTypeCode: MODIFY_SELECT_LINES_CONSTANTS.PRE_PAID,
      customerId: this.stateStoreUtilsService.getBscsCustomerID(),
      msisdn: msisdn,
    };
    return this.apiGateway.postRequest(API_MAP.API_2827_postchangeMsisdnEligibility, payload).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      catchError((error: APIError[]) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Verify if Manager override is allowed.
   * If Account activation date <= otpCutoff Date then true
   * else return Manager Override status flag
   */
  public isManagerOverrideAllowed(
    customerAccountBillingDetails: CustomerAccountBillingDetails
  ): boolean {
    if (this.featureToggleService.isOTPVerificationEnabled() === true) {
      const otpCutoff = this.appConfig.getConfig('otpCutoffDate');
      const isAccountActivationDateAfterOTPCutoff = this.isAccountActivationDateAfterOTPCutoff(
        otpCutoff,
        customerAccountBillingDetails
      );
      this.stateStoreUtilsService.setIsAccountActivationAfterOTPCutoff(
        isAccountActivationDateAfterOTPCutoff
      );
      if (isAccountActivationDateAfterOTPCutoff) {
        return !!this.featureToggleService.isManagerOverrideEnabled();
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  /**
   * Compare Account Activation Date vs OTP Cutoff Date
   * @param otpCutoff
   * @private
   */
  public isAccountActivationDateAfterOTPCutoff(
    otpCutoff: string,
    customerAccountBillingDetails: CustomerAccountBillingDetails
  ): boolean {
    if (
      customerAccountBillingDetails &&
      customerAccountBillingDetails.accountActivationDate &&
      otpCutoff
    ) {
      if (new Date(customerAccountBillingDetails.accountActivationDate) > new Date(otpCutoff)) {
        return true;
      }
    }
    return false;
  }
}
